import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaChevronLeft } from 'react-icons/fa';
import Helper from '../services/helper.service';
import { useConfirm } from '../components/confirm-dialog';
import { getSociety, saveSociety, deleteSociety, getAllBanks } from '../services/api.service';

// -------------------- Utils --------------------
const defaultSociety = {
    name: '',
    logoUrl: '',
    website: '',
    email: '',
    phone: '',
    siret: '',
    vatNumber: '',
    status: 'active',
    address: { line1: '', line2: '', postalCode: '', city: '', country: 'France' },
    notes: '',
    legalForm: 'SAS',
    shareCapital: 0,
    rcsCity: '',
    rcsNumber: '',
    bankId: '',
    bank: { name: '', iban: '', bic: '' },
    invoice: { prefix: 'FG', suffix: '' },
};

const normalizeIBAN = (v = '') => v.replace(/\s+/g, '').toUpperCase();
const emailOK = (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const phoneOK = (v) => !v || /^[0-9+() .-]{6,}$/.test(v);
const siretOK = (v) => !v || /^\d{14}$/.test(v);
const ibanOK = (v) => {
    if (!v) return true;
    const n = normalizeIBAN(v);
    return /^[A-Z]{2}[0-9A-Z]{13,34}$/.test(n);
};
const bicOK = (v) => !v || /^[A-Z0-9]{8}([A-Z0-9]{3})?$/.test((v || '').toUpperCase());

const formatEuro = (n) => {
    if (n === null || n === undefined || n === '' || isNaN(Number(n))) return '';
    return new Intl.NumberFormat('fr-FR').format(Number(n)) + ' €';
};

// Map a field key to its tab for auto-switching focus
const FIELD_TAB = {
    name: 'general',
    status: 'general',
    siret: 'general',

    email: 'contact',
    phone: 'contact',

    'address.city': 'address',
    'address.line1': 'address',

    'bank.iban': 'invoice',
    'bank.bic': 'invoice',
    'bank.name': 'invoice',
    'invoice.prefix': 'invoice',
};

// -------------------- Component --------------------
const SocietyEditorPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const confirm = useConfirm();
    const isEdit = Boolean(id && id !== 'new');

    const [society, setSociety] = useState(null);
    const [banks, setBanks] = useState([]);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('general'); // general | contact | address | invoice | notes
    const [errors, setErrors] = useState({});

    // ---------- Load ----------
    useEffect(() => {
        const run = async () => {
            try {
                const allBanks = await getAllBanks();
                setBanks(Array.isArray(allBanks) ? allBanks : []);
            } catch {
                Helper.toast('Impossible de charger les banques', 'error');
            }

            if (isEdit) {
                try {
                    const data = await getSociety(id);
                    setSociety({
                        ...defaultSociety,
                        ...(data || {}),
                        address: { ...defaultSociety.address, ...(data?.address || {}) },
                        bank: { ...defaultSociety.bank, ...(data?.bank || {}) },
                        bankId: data?.bankId?._id || data?.bankId || '',
                        invoice: { ...defaultSociety.invoice, ...(data?.invoice || {}) },
                    });
                } catch {
                    Helper.toast('Impossible de charger la société', 'error');
                    navigate('/societies');
                }
            } else {
                setSociety(defaultSociety);
            }
        };
        run();
    }, [id, isEdit, navigate]);

    // ---------- Updaters ----------
    const setField = (name, value) => {
        setSociety((s) => ({ ...s, [name]: value }));
        setErrors((e) => ({ ...e, [name]: undefined }));
    };
    const setAddr = (k, v) => {
        setSociety((s) => ({ ...s, address: { ...s.address, [k]: v } }));
        setErrors((e) => ({ ...e, [`address.${k}`]: undefined }));
    };
    const setBank = (k, v) => {
        setSociety((s) => ({ ...s, bank: { ...s.bank, [k]: v } }));
        setErrors((e) => ({ ...e, [`bank.${k}`]: undefined }));
    };
    const setInvoice = (k, v) => {
        setSociety((s) => ({ ...s, invoice: { ...s.invoice, [k]: v } }));
        setErrors((e) => ({ ...e, [`invoice.${k}`]: undefined }));
    };

    // ---------- Derived previews ----------
    const legalPreview = useMemo(() => {
        const parts = [];
        if (society?.legalForm) parts.push(society.legalForm);
        if (society?.shareCapital) parts.push(`au capital de ${formatEuro(society.shareCapital)}`);
        if (society?.siret) parts.push(`SIRET ${society.siret}`);
        if (society?.rcsCity || society?.rcsNumber) {
            const city = society.rcsCity ? society.rcsCity : '';
            const num = society.rcsNumber ? society.rcsNumber : '';
            parts.push(`RCS ${city}${city && num ? ' ' : ''}${num}`);
        }
        if (society?.vatNumber) parts.push(`N° de TVA intracommunautaire ${society.vatNumber}`);
        return parts.join(' - ');
    }, [society]);

    const bankPreview = useMemo(() => {
        const b = society?.bank || {};
        if (!b.name && !b.iban && !b.bic) return '';
        const ib = b.iban ? `IBAN : ${b.iban}` : '';
        const bc = b.bic ? `BIC : ${b.bic}` : '';
        return [b.name, ib, bc].filter(Boolean).join(' - ');
    }, [society]);

    const invoiceNumberExample = useMemo(() => {
        const prefix = society?.invoice?.prefix?.trim();
        const suffix = society?.invoice?.suffix?.trim();
        return `${prefix ? `${prefix}-` : ''}12345${suffix ? `-${suffix}` : ''}`;
    }, [society]);

    // ---------- Validation ----------
    const validate = () => {
        const e = {};

        // GENERAL
        if (!society.name?.trim()) e.name = 'Le nom est requis';
        if (!siretOK(society.siret)) e.siret = 'SIRET invalide (14 chiffres)';

        // CONTACT
        if (!emailOK(society.email)) e.email = 'Email invalide';
        if (!phoneOK(society.phone)) e.phone = 'Numéro invalide';

        // ADDRESS (optional example: if line1 provided, city required)
        if (society.address?.line1?.trim() && !society.address?.city?.trim()) {
            e['address.city'] = 'Ville requise si l\'adresse est renseignée';
        }

        // INVOICE
        if (!society.invoice?.prefix?.trim()) e['invoice.prefix'] = 'Préfixe requis';
        if (!ibanOK(society.bank?.iban)) e['bank.iban'] = 'IBAN invalide';
        if (!bicOK(society.bank?.bic)) e['bank.bic'] = 'BIC invalide';

        setErrors(e);

        // Switch to the first tab that contains an error
        const firstField = Object.keys(e)[0];
        if (firstField) {
            const tab = FIELD_TAB[firstField] || 'general';
            setActiveTab(tab);
        }

        return Object.keys(e).length === 0;
    };

    // ---------- Save/Delete ----------
    const onSave = async () => {
        if (!validate()) {
            Helper.toast('Merci de corriger les champs en rouge.', 'error');
            return;
        }

        setSaving(true);
        try {
            // Sanitize payload: trim strings, normalize numbers, avoid empty string for ObjectId
            const payload = {
                ...society,
                name: society.name?.trim(),
                email: (society.email || '').trim().toLowerCase(),
                website: (society.website || '').trim(),
                phone: (society.phone || '').trim(),
                siret: (society.siret || '').trim(),
                vatNumber: (society.vatNumber || '').trim(),
                status: society.status || 'active',
                shareCapital: Number(society.shareCapital) || 0,
                address: { ...(society.address || {}) },
                bank: {
                    ...(society.bank || {}),
                    iban: normalizeIBAN(society.bank?.iban || ''),
                    bic: (society.bank?.bic || '').toUpperCase(),
                },
                invoice: { ...(society.invoice || {}) },
                bankId:
                    society.bankId && typeof society.bankId === 'object'
                        ? society.bankId._id
                        : society.bankId || undefined,
            };

            await saveSociety(payload);
            Helper.toast('Société enregistrée', 'success');
            navigate('/societies');
        } catch (e) {
            Helper.toast("Erreur lors de l'enregistrement", 'error');
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async () => {
        if (!society?._id) return;
        const ok = await confirm({
            title: 'Suppression',
            message: (
                <>
                    Supprimer la société <strong>{society.name || '(Sans nom)'}</strong> ?
                </>
            ),
            confirmText: 'Oui',
            cancelText: 'Non',
            tone: 'warning',
        });
        if (!ok) return Helper.toast('Action annulée', 'info');

        try {
            await deleteSociety(society);
            navigate('/societies');
        } catch (e) {
            Helper.toast('Erreur lors de la suppression', 'error');
        }
    };

    if (!society) return <div className="p-6 text-center text-gray-500">Chargement…</div>;

    return (
        <div className="max-w-6xl mx-auto p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <button onClick={() => navigate('/societies')} className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900">
                    <FaChevronLeft className="h-4 w-4" /> Retour
                </button>
                <div className="flex items-center gap-3">
                    {society._id && (
                        <button onClick={onDelete} className="px-4 py-2 rounded border border-red-200 text-red-700 hover:bg-red-50">Supprimer</button>
                    )}
                    <button
                        onClick={onSave}
                        disabled={saving}
                        className={`px-4 py-2 rounded text-white shadow ${saving ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                    >
                        {saving ? 'Enregistrement…' : 'Enregistrer'}
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b mb-4">
                {['general', 'contact', 'address', 'invoice', 'notes'].map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-2 -mb-px ${activeTab === tab ? 'border-b-2 border-blue-600 font-semibold text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        {tab === 'general' && 'Général'}
                        {tab === 'contact' && 'Contact'}
                        {tab === 'address' && 'Adresse'}
                        {tab === 'invoice' && 'Facturation'}
                        {tab === 'notes' && 'Notes'}
                    </button>
                ))}
            </div>

            {/* GENERAL */}
            {activeTab === 'general' && (
                <div className="rounded-2xl border bg-white shadow-sm p-5 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">Nom <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                value={society.name}
                                onChange={(e) => setField('name', e.target.value)}
                                className={`w-full border rounded px-3 py-2 shadow-sm ${errors.name ? 'border-red-500' : ''}`}
                                placeholder="Nom de la société"
                            />
                            {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                        </div>
                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">Logo (URL)</label>
                            <input
                                type="text"
                                value={society.logoUrl}
                                onChange={(e) => setField('logoUrl', e.target.value)}
                                className="w-full border rounded px-3 py-2 shadow-sm"
                                placeholder="https://..."
                            />
                        </div>
                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">Site web</label>
                            <input
                                type="url"
                                value={society.website}
                                onChange={(e) => setField('website', e.target.value)}
                                className="w-full border rounded px-3 py-2 shadow-sm"
                                placeholder="https://exemple.com"
                            />
                        </div>
                        <div className="text-center">
                            <label className="block mb-1 text-sm font-medium text-gray-700">Statut</label>
                            <div className="inline-flex rounded-lg border bg-white overflow-hidden">
                                {[
                                    { v: 'active', label: 'Active' },
                                    { v: 'inactive', label: 'Inactive' },
                                ].map((opt) => (
                                    <button
                                        key={opt.v}
                                        type="button"
                                        onClick={() => setField('status', opt.v)}
                                        className={`px-3 py-2 text-sm ${(society.status || 'active') === opt.v ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 text-gray-700'}`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">SIRET</label>
                            <input
                                type="text"
                                value={society.siret}
                                onChange={(e) => setField('siret', e.target.value)}
                                className={`w-full border rounded px-3 py-2 shadow-sm ${errors.siret ? 'border-red-500' : ''}`}
                                placeholder="14 chiffres"
                            />
                            {errors.siret && <p className="mt-1 text-xs text-red-600">{errors.siret}</p>}
                        </div>
                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">TVA intracommunautaire</label>
                            <input
                                type="text"
                                value={society.vatNumber}
                                onChange={(e) => setField('vatNumber', e.target.value)}
                                className="w-full border rounded px-3 py-2 shadow-sm"
                                placeholder="FRXX999999999"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* CONTACT */}
            {activeTab === 'contact' && (
                <div className="rounded-2xl border bg-white shadow-sm p-5 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">Email</label>
                            <input
                                type="email"
                                value={society.email}
                                onChange={(e) => setField('email', e.target.value)}
                                className={`w-full border rounded px-3 py-2 shadow-sm ${errors.email ? 'border-red-500' : ''}`}
                                placeholder="contact@exemple.com"
                            />
                            {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                        </div>
                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">Téléphone</label>
                            <input
                                type="text"
                                value={society.phone}
                                onChange={(e) => setField('phone', e.target.value)}
                                className={`w-full border rounded px-3 py-2 shadow-sm ${errors.phone ? 'border-red-500' : ''}`}
                                placeholder="+33 1 23 45 67 89"
                            />
                            {errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone}</p>}
                        </div>
                    </div>
                </div>
            )}

            {/* ADDRESS */}
            {activeTab === 'address' && (
                <div className="rounded-2xl border bg-white shadow-sm p-5 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">Adresse (ligne 1)</label>
                            <input
                                type="text"
                                value={society.address.line1}
                                onChange={(e) => setAddr('line1', e.target.value)}
                                className="w-full border rounded px-3 py-2 shadow-sm"
                            />
                        </div>
                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">Adresse (ligne 2)</label>
                            <input
                                type="text"
                                value={society.address.line2}
                                onChange={(e) => setAddr('line2', e.target.value)}
                                className="w-full border rounded px-3 py-2 shadow-sm"
                            />
                        </div>
                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">Code postal</label>
                            <input
                                type="text"
                                value={society.address.postalCode}
                                onChange={(e) => setAddr('postalCode', e.target.value)}
                                className="w-full border rounded px-3 py-2 shadow-sm"
                            />
                        </div>
                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">Ville</label>
                            <input
                                type="text"
                                value={society.address.city}
                                onChange={(e) => setAddr('city', e.target.value)}
                                className={`w-full border rounded px-3 py-2 shadow-sm ${errors['address.city'] ? 'border-red-500' : ''}`}
                            />
                            {errors['address.city'] && <p className="mt-1 text-xs text-red-600">{errors['address.city']}</p>}
                        </div>
                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">Pays</label>
                            <input
                                type="text"
                                value={society.address.country}
                                onChange={(e) => setAddr('country', e.target.value)}
                                className="w-full border rounded px-3 py-2 shadow-sm"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* INVOICE */}
            {activeTab === 'invoice' && (
                <>
                    <div className="rounded-2xl border bg-white shadow-sm p-5 space-y-8">
                        {/* Legal */}
                        <div>
                            <h3 className="font-semibold text-gray-800 mb-3">Informations légales</h3>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-gray-700">Forme juridique</label>
                                    <input
                                        type="text"
                                        value={society.legalForm}
                                        onChange={(e) => setField('legalForm', e.target.value)}
                                        className="w-full border rounded px-3 py-2 shadow-sm"
                                        placeholder="SAS, SARL…"
                                    />
                                </div>
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-gray-700">Capital social (€)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={society.shareCapital}
                                        onChange={(e) => setField('shareCapital', e.target.value)}
                                        className="w-full border rounded px-3 py-2 shadow-sm"
                                        placeholder="4000"
                                    />
                                </div>
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-gray-700">RCS (Ville)</label>
                                    <input
                                        type="text"
                                        value={society.rcsCity}
                                        onChange={(e) => setField('rcsCity', e.target.value)}
                                        className="w-full border rounded px-3 py-2 shadow-sm"
                                        placeholder="Paris"
                                    />
                                </div>
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-gray-700">RCS (N°)</label>
                                    <input
                                        type="text"
                                        value={society.rcsNumber}
                                        onChange={(e) => setField('rcsNumber', e.target.value)}
                                        className="w-full border rounded px-3 py-2 shadow-sm"
                                        placeholder="832291348"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Bank display on invoice */}
                        <div>
                            <h3 className="font-semibold text-gray-800 mb-3">Coordonnées bancaires</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-gray-700">Banque</label>
                                    <input
                                        type="text"
                                        value={society.bank.name}
                                        onChange={(e) => setBank('name', e.target.value)}
                                        className={`w-full border rounded px-3 py-2 shadow-sm ${errors['bank.name'] ? 'border-red-500' : ''}`}
                                        placeholder="Banque Populaire"
                                    />
                                    {errors['bank.name'] && <p className="mt-1 text-xs text-red-600">{errors['bank.name']}</p>}
                                </div>
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-gray-700">IBAN</label>
                                    <input
                                        type="text"
                                        value={society.bank.iban}
                                        onChange={(e) => setBank('iban', e.target.value)}
                                        className={`w-full border rounded px-3 py-2 shadow-sm ${errors['bank.iban'] ? 'border-red-500' : ''}`}
                                        placeholder="FR76 1470 7000 2632 0212 5444 918"
                                    />
                                    {errors['bank.iban'] && <p className="mt-1 text-xs text-red-600">{errors['bank.iban']}</p>}
                                </div>
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-gray-700">BIC</label>
                                    <input
                                        type="text"
                                        value={society.bank.bic}
                                        onChange={(e) => setBank('bic', e.target.value)}
                                        className={`w-full border rounded px-3 py-2 shadow-sm ${errors['bank.bic'] ? 'border-red-500' : ''}`}
                                        placeholder="CCBPFRPPMTZ"
                                    />
                                    {errors['bank.bic'] && <p className="mt-1 text-xs text-red-600">{errors['bank.bic']}</p>}
                                </div>
                            </div>
                        </div>

                        {/* Invoice numbering */}
                        <div>
                            <h3 className="font-semibold text-gray-800 mb-3">Numérotation des factures</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-gray-700">Préfixe <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        value={society.invoice?.prefix || ''}
                                        onChange={(e) => setInvoice('prefix', e.target.value)}
                                        className={`w-full border rounded px-3 py-2 shadow-sm ${errors['invoice.prefix'] ? 'border-red-500' : ''}`}
                                        placeholder="FG"
                                    />
                                    {errors['invoice.prefix'] && <p className="mt-1 text-xs text-red-600">{errors['invoice.prefix']}</p>}
                                </div>
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-gray-700">Suffixe</label>
                                    <input
                                        type="text"
                                        value={society.invoice?.suffix || ''}
                                        onChange={(e) => setInvoice('suffix', e.target.value)}
                                        className="w-full border rounded px-3 py-2 shadow-sm"
                                        placeholder="2025, PARIS, …"
                                    />
                                </div>
                                <div className="self-end">
                                    <div className="text-sm text-gray-600">Exemple:&nbsp;
                                        <span className="font-medium text-gray-800">{invoiceNumberExample}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Live preview */}
                        <div className="rounded-xl border bg-gray-50 p-4">
                            <p className="text-sm font-medium text-gray-700 mb-2">Aperçu (ligne juridique) :</p>
                            <div className="text-sm text-gray-800 break-words">{legalPreview || '—'}</div>

                            <p className="text-sm font-medium text-gray-700 mt-4 mb-2">Aperçu (ligne banque) :</p>
                            <div className="text-sm text-gray-800 break-words">{bankPreview || '—'}</div>
                        </div>
                    </div>

                    {/* Gateway binding (bankId) */}
                    <div className="rounded-2xl border bg-white shadow-sm p-5 space-y-3 mt-6">
                        <h3 className="font-semibold text-gray-800">Banque liée (paiement)</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                            <div className="md:col-span-2">
                                <label className="block mb-1 text-sm font-medium text-gray-700">Sélectionner une banque</label>
                                <select
                                    value={society.bankId || ''}
                                    onChange={(e) => setField('bankId', e.target.value || '')}
                                    className="w-full border rounded px-3 py-2 shadow-sm bg-white"
                                >
                                    <option value="">Aucune banque liée</option>
                                    {banks.map((b) => (
                                        <option key={b._id} value={b._id}>
                                            {(b.name?.fr || b.name || 'Banque') + (b.useByDefault ? ' (par défaut)' : '')}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <p className="text-xs text-gray-500">Cette sélection lie la société à une configuration de passerelle de paiement (shopId, clés, IPN).</p>
                    </div>
                </>
            )}

            {/* NOTES */}
            {activeTab === 'notes' && (
                <div className="rounded-2xl border bg-white shadow-sm p-5 space-y-4">
                    <label className="block mb-1 text-sm font-medium text-gray-700">Notes internes</label>
                    <textarea
                        value={society.notes}
                        onChange={(e) => setField('notes', e.target.value)}
                        className="w-full min-h-[160px] border rounded px-3 py-2 shadow-sm"
                        placeholder="Informations complémentaires, remarques internes, etc."
                    />
                </div>
            )}
        </div>
    );
};

export default SocietyEditorPage;